import {
  provideDatalayerOutputs,
  provideDatalayerSideEffects,
} from '@analytics/datalayer';
import {
  AnalyticsEnhancerSources,
  FEATURE_ANALYTICS_ENHANCERS,
  ROOT_ANALYTICS_ENHANCERS,
} from '@analytics/enhancers';
import {
  TrackErrorDirective,
  TrackFieldfocusDirective,
  TrackImpressionDirective,
  TrackInteractionDirective,
  TrackLinkDirective,
  TrackProductDirective,
  TrackStepDirective,
  TrackValueChangeDirective,
} from '@analytics/events';
import type {
  AnalyticsEnhancer,
  DatalayerOutput,
  DatalayerStreamSideEffect,
} from '@analytics/models';
import type { ModuleWithProviders, Type } from '@angular/core';
import { NgModule } from '@angular/core';
import { CookieService } from 'ngx-cookie-service';

import { AnalyticsFeatureModule } from './analytics-feature.module';
import { AnalyticsRootModule } from './analytics-root.module';

// --- Migrated directives ---
// These directives are migrated to the new analytics directive setup:
// - imported via `@analytics/events`.
// - standalone directive
// - using the datalayer service in it's internals.
const migratedDirectives = [
  TrackInteractionDirective,
  TrackLinkDirective,
  TrackImpressionDirective,
  TrackProductDirective,
  TrackErrorDirective,
  TrackFieldfocusDirective,
  TrackStepDirective,
  TrackValueChangeDirective,
];

@NgModule({
  declarations: [],
  providers: [CookieService],
  imports: [...migratedDirectives],
  exports: [...migratedDirectives],
})
export class AnalyticsModule {
  static forFeature({
    enhancers = [],
  }: {
    enhancers: Type<AnalyticsEnhancer>[];
  }): ModuleWithProviders<AnalyticsFeatureModule> {
    return {
      ngModule: AnalyticsFeatureModule,
      providers: [
        enhancers,
        {
          provide: FEATURE_ANALYTICS_ENHANCERS,
          multi: true,
          deps: enhancers,
          useFactory: createSourceInstances,
        },
      ],
    };
  }

  static forRoot({
    enhancers = [],
    sideEffects = [],
    outputs = [],
  }: {
    enhancers: Type<AnalyticsEnhancer>[];
    sideEffects: Type<DatalayerStreamSideEffect>[];
    outputs: Type<DatalayerOutput>[];
  }): ModuleWithProviders<AnalyticsRootModule> {
    return {
      ngModule: AnalyticsRootModule,
      providers: [
        AnalyticsEnhancerSources,
        enhancers,
        sideEffects,
        outputs,
        {
          provide: ROOT_ANALYTICS_ENHANCERS,
          deps: enhancers,
          useFactory: createSourceInstances,
        },
        provideDatalayerSideEffects(sideEffects),
        provideDatalayerOutputs(outputs),
      ],
    };
  }
}

export function createSourceInstances<T>(...instances: T[]) {
  return instances;
}
